import { v4 } from 'uuid';
import {
  defaultClosingRange,
  defaultCompoundStepCount,
  defaultCompoundStepCountOperator,
  perStepClosingRange,
} from '../../constants/compound';
import { createFilterReducer } from '../filter';
import {
  dashboardFunnelStepFilterActions,
  dashboardFunnelStepFilterActionsPrefix,
} from '../../constants/filter';

const stepFilterReducer = createFilterReducer(dashboardFunnelStepFilterActionsPrefix);

const defaultStep = {
  filter: stepFilterReducer(undefined, {}),
  closingRange: defaultClosingRange,
  enabled: true,
  count: defaultCompoundStepCount,
  countOperator: defaultCompoundStepCountOperator,
  id: v4(),
  legendName: '',
};

const defaultFunnel = {
  steps: [defaultStep],
  closingRange: perStepClosingRange,
};

const updateInitialState = {
  funnel: defaultFunnel,
  updating: false,
  id: undefined,
  options: {
    conversionInterval: 'NONE',
    view: 'Chart',
  },
};

const pureUpdateStep = (funnelSteps, stepIndex, newProps) => {
  return [
    ...funnelSteps.slice(0, stepIndex),
    {
      ...funnelSteps[stepIndex],
      ...newProps,
    },
    ...funnelSteps.slice(stepIndex + 1),
  ];
};

const pureUpdateSteps = (funnelSteps, stepUpdater) => {
  return funnelSteps.map((s, i) => stepUpdater(s, i));
};

const changeStepPosition = (steps, oldId, newId) => {
  if (oldId === newId) {
    return steps;
  }

  let result;

  if (oldId < newId) {
    result = [
      ...steps.slice(0, oldId),
      ...steps.slice(oldId + 1, newId + 1),
      undefined,
      ...steps.slice(newId + 1),
    ];
  } else {
    result = [
      ...steps.slice(0, newId),
      undefined,
      ...steps.slice(newId, oldId),
      ...steps.slice(oldId + 1),
    ];
  }

  result[newId] = { ...steps[oldId] };
  return result;
};

const toggleStepEnabled = (steps, stepIndex) => [
  ...steps.slice(0, stepIndex),
  {
    ...steps[stepIndex],
    enabled: !steps[stepIndex].enabled,
  },
  ...steps.slice(stepIndex + 1),
];

const funnelsCompose = (state = updateInitialState, action) => {
  switch (action.type) {
    case 'DASHBOARD_SELECT_FUNNEL':
      return updateInitialState;

    case 'DASHBOARD_START_UPDATE_FUNNEL': {
      const funnel = action.funnel || defaultFunnel;

      return {
        ...state,
        funnel: { ...funnel, steps: funnel.steps.map((step) => ({ ...step, id: v4() })) },
        id: action.funnel ? action.funnel.id : undefined,
      };
    }

    case 'DASHBOARD_CANCEL_UPDATE_FUNNEL':
      return updateInitialState;

    case 'DASHBOARD_UPDATE_SET_FUNNEL_NAME':
      return {
        ...state,
        funnel: { ...state.funnel, name: action.name },
      };

    case 'DASHBOARD_UPDATE_FUNNEL':
      return {
        ...state,
        updating: true,
      };

    case 'DASHBOARD_UPDATE_FUNNEL_SUCCESS':
      return updateInitialState;

    case 'DASHBOARD_UPDATE_FUNNEL_FAILURE':
      return {
        ...state,
        updating: false,
      };

    case 'DASHBOARD_DELETE_FUNNEL_SUCCESS':
      return updateInitialState;

    case 'DASHBOARD_UPDATE_FUNNEL_ADD_STEP':
      return {
        ...state,
        funnel: { ...state.funnel, steps: [...state.funnel.steps, { ...defaultStep, id: v4() }] },
      };

    case 'DASHBOARD_UPDATE_FUNNEL_CHANGE_STEP_POSITION':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: changeStepPosition(state.funnel.steps, action.oldIndex, action.newIndex),
        },
      };

    case 'DASHBOARD_UPDATE_FUNNEL_SET_EVENT_NAME':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: pureUpdateStep(state.funnel.steps, action.stepIndex, {
            event: action.event,
            filter: stepFilterReducer(undefined, {}),
          }),
        },
      };

    case 'DASHBOARD_UPDATE_FUNNEL_SET_LEGEND_NAME':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: pureUpdateStep(state.funnel.steps, action.stepIndex, {
            legendName: action.name,
            filter: stepFilterReducer(undefined, {}),
          }),
        },
      };

    case 'DASHBOARD_UPDATE_TOGGLE_FUNNEL_ENABLED':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: toggleStepEnabled(state.funnel.steps, action.stepIndex),
        },
      };

    case 'DASHBOARD_UPDATE_FUNNEL_SET_STEP_OPTIONAL_STATUS':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: pureUpdateSteps(state.funnel.steps, (s, i) => {
            if (i === action.stepIndex) {
              return {
                ...s,
                optional: action.status,
              };
            }

            if (action.status && s.optional) {
              return {
                ...s,
                optional: false,
              };
            }

            return s;
          }),
        },
      };

    case 'DASHBOARD_UPDATE_FUNNEL_SET_STEP_COUNT':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: pureUpdateStep(state.funnel.steps, action.stepIndex, {
            count: action.count,
            countOperator: action.operator,
          }),
        },
      };

    case 'DASHBOARD_UPDATE_FUNNEL_SET_STEP_CLOSING_RANGE':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: pureUpdateStep(state.funnel.steps, action.stepIndex, {
            closingRange: action.closingRange,
          }),
        },
      };

    case 'DASHBOARD_UPDATE_REMOVE_FUNNEL_STEP':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: [
            ...state.funnel.steps.slice(0, action.stepIndex),
            ...state.funnel.steps.slice(action.stepIndex + 1),
          ],
        },
      };

    case 'DASHBOARD_UPDATE_FUNNEL_SET_GLOBAL_CLOSING_RANGE':
      return {
        ...state,
        funnel: {
          ...state.funnel,
          closingRange: action.closingRange,
        },
      };

    case 'DASHBOARD_FUNNEL_SELECT_DUPLICATE_FUNNEL':
      return {
        ...updateInitialState,
        funnel: {
          ...structuredClone(action.item),
          name: `${action.item.name}_copy`,
          id: undefined,
        },
      };

    case 'DASHBOARD_SET_CONVERSION_INTERVAL':
      return {
        ...state,
        options: {
          ...state.options,
          conversionInterval: action.conversionInterval,
        },
      };

    case 'DASHBOARD_CHANGE_FUNNEL_VIEW':
      return {
        ...state,
        options: {
          ...state.options,
          view: action.view,
        },
      };

    case dashboardFunnelStepFilterActions.ADD_FILTER_CLAUSE:
    case dashboardFunnelStepFilterActions.REMOVE_FILTER_CLAUSE:
    case dashboardFunnelStepFilterActions.SET_FILTER_CLAUSE_FIELD:
    case dashboardFunnelStepFilterActions.SET_FILTER_CLAUSE_VALUE:
    case dashboardFunnelStepFilterActions.SET_CLAUSES_CONCAT_OPERATOR:
    case dashboardFunnelStepFilterActions.SET_FILTER_CLAUSE_OPERATOR:
      return {
        ...state,
        funnel: {
          ...state.funnel,
          steps: pureUpdateStep(state.funnel.steps, action.stepIndex, {
            filter: stepFilterReducer(state.funnel.steps[action.stepIndex].filter, action),
          }),
        },
      };

    default:
      return state;
  }
};

export default funnelsCompose;

export const isUpdating = (state) => state.updating;

export const getUpdating = (state) => state.funnel;

export const getUpdatingId = (state) => state.id;

export const getFunnelConversionInterval = (state) => state.options.conversionInterval;

export const getFunnelView = (state) => state.options.view;
